import { useApplyPremiumDeductions } from '@client/components/ltc-financial-planning/hooks/useApplyPremiumDeductions';
import { useLtcFinancialPlanningNavigation } from '@client/components/ltc-financial-planning/hooks/useLtcFinancialPlanningNavigation';
import { putLtcFinancialPlanningStateByClientIdRequest } from '@client/model';
import { selectClient, selectLtcFinancialPlanning } from '@client/model/selectors';
import { isPolicyAddedViaShopInsurance } from '@client/util/policyFundingSourceUtils';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Hook to handle the "Continue" action with automatic
 * premium deduction and navigation logic.
 *
 * When continuing from the comparison step with a policy:
 * 1. Applies premium deductions to self-funding sources
 * 2. Waits for the save operation to complete
 * 3. Navigates back to the old financing flow
 *
 * @returns Object with handleContinue function and isSaving state
 */
export function useContinueWithDeductions() {
  const navigation = useLtcFinancialPlanningNavigation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId: string | undefined }>();
  const client = useSelector(selectClient);
  const ltcFinancialPlanningState = useSelector(selectLtcFinancialPlanning);
  const { applyDeductions, isSaving } = useApplyPremiumDeductions();
  const [shouldNavigateAfterSave, setShouldNavigateAfterSave] = useState(false);

  // Check if user has a policy added via shop insurance
  const hasPolicy = useMemo(() => {
    const ltcPolicies = client?.multipleFundingSources?.ltcPolicy || {};
    return Object.values(ltcPolicies).some(policy => isPolicyAddedViaShopInsurance(policy));
  }, [client]);

  // Navigate after premium deductions are saved and ltc state is saved
  useEffect(() => {
    if (shouldNavigateAfterSave && !isSaving && !ltcFinancialPlanningState.isLoading) {
      setShouldNavigateAfterSave(false);
      // If on comparison step (final step), navigate back to old financing flow
      if (navigation.currentStep === 'comparison') {
        navigate(`/clients/${clientId}/onboarding/funding-sources?fromLtcPlanning=true`);
      } else {
        navigation.goNext();
      }
    }
  }, [shouldNavigateAfterSave, isSaving, ltcFinancialPlanningState.isLoading, navigation, navigate, clientId]);

  /**
   * Handle continue action - saves ltc financial planning state, applies deductions if on comparison step with policy,
   * then navigates back to old financing flow. For other steps, navigates to next step.
   */
  const handleContinueWithDeductions = () => {
    // Save current ltc financial planning state before continuing
    if (clientId) {
      const { isLoading, ...stateToSave } = ltcFinancialPlanningState;
      dispatch(
        putLtcFinancialPlanningStateByClientIdRequest({
          clientId,
          stateData: stateToSave,
        }),
      );
    }

    // Apply premium deductions if on comparison step with a policy
    if (navigation.currentStep === 'comparison' && hasPolicy) {
      applyDeductions();
      setShouldNavigateAfterSave(true);
    } else if (navigation.currentStep === 'comparison') {
      navigate(`/clients/${clientId}/onboarding/funding-sources?fromLtcPlanning=true`);
    } else {
      setShouldNavigateAfterSave(true);
    }
  };

  return {
    handleContinueWithDeductions,
    isSaving,
    shouldApplyDeductions: navigation.currentStep === 'comparison' && hasPolicy,
  };
}
